import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "totalCount", "pollResults",
    "detailPanel", "detailBackdrop", "detailSlide",
    "detailName", "detailMeta", "detailContent",
    "resetButton", "resetModal", "resetModalName"
  ]

  static values = {
    activityId: String,
    inloopId: String,
    pollUrl: String,
    resetUrl: String,
    formType: String,
    fields: Array
  }

  connect() {
    this.responsesCache = null
    this.fetchResponses()
  }

  disconnect() {
  }

  async fetchResponses() {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.pollUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": token
        }
      })

      if (!response.ok) return

      const data = await response.json()
      this.responsesCache = data.responses

      // Update total count
      if (this.hasTotalCountTarget) {
        this.totalCountTarget.textContent = data.total_count
      }

      // Update poll results if polling type
      if (this.formTypeValue === "polling" && data.poll_results && this.hasPollResultsTarget) {
        this.updatePollChart(data.poll_results, data.total_count)
      }
    } catch (err) {
      console.error("Error fetching form responses:", err)
    }
  }

  updatePollChart(pollResults, totalCount) {
    Object.entries(pollResults).forEach(([fieldId, result]) => {
      const container = this.pollResultsTarget.querySelector(`[data-poll-field-id="${fieldId}"]`)
      if (!container) return

      result.options.forEach(opt => {
        const pct = totalCount > 0 ? ((opt.count / totalCount) * 100).toFixed(1) : 0
        const bars = container.querySelectorAll(".flex.items-center.gap-3")
        bars.forEach(bar => {
          const label = bar.querySelector("span")
          if (label && label.textContent.trim() === opt.label) {
            const progressBar = bar.querySelector("[style*='width']")
            const countSpan = bar.querySelector(".text-right")
            if (progressBar) progressBar.style.width = `${pct}%`
            if (countSpan) countSpan.textContent = `${opt.count} (${pct}%)`
          }
        })
      })
    })
  }

  // ==================
  // Response Detail
  // ==================

  showDetail(e) {
    const row = e.currentTarget
    const userId = row.dataset.userId
    if (!userId) return

    // Find response data from cache or parse from table
    const responseData = this.responsesCache?.find(r => r.user_id === userId)
    if (!responseData) return

    this._currentUserId = userId
    this.renderDetail(responseData)
    this.openPanel()
  }

  renderDetail(response) {
    // Header info
    if (this.hasDetailNameTarget) {
      this.detailNameTarget.textContent = response.user_name || "Unknown"
    }
    if (this.hasDetailMetaTarget) {
      const parts = []
      if (response.user_email) parts.push(response.user_email)
      if (response.submitted_at) {
        try {
          const date = new Date(response.submitted_at)
          parts.push(`Submitted ${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at ${date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`)
        } catch {
          parts.push(`Submitted ${response.submitted_at}`)
        }
      }
      this.detailMetaTarget.textContent = parts.join(" · ")
    }

    // Build field answers
    if (this.hasDetailContentTarget) {
      const fields = this.fieldsValue || []
      const answers = response.answers || {}

      this.detailContentTarget.innerHTML = `
        <div class="flex flex-col gap-5">
          ${fields.map((field, idx) => {
            const answer = answers[field.id]
            return this.renderDetailField(field, answer, idx + 1)
          }).join("")}
        </div>
      `
    }
  }

  renderDetailField(field, answer, index) {
    let displayValue = ""

    if (answer === null || answer === undefined || answer === "") {
      displayValue = `<span class="text-gray-400 italic text-sm">No answer</span>`
    } else if (field.type === "rating") {
      const rating = parseInt(answer) || 0
      displayValue = `
        <div class="flex items-center gap-1">
          ${[1,2,3,4,5].map(s =>
            `<span class="text-lg ${s <= rating ? "text-amber-400" : "text-gray-200"}">★</span>`
          ).join("")}
          <span class="ml-2 text-sm font-medium text-gray-600">${rating}/5</span>
        </div>
      `
    } else if (field.type === "checkbox") {
      const checked = answer === true || answer === "true"
      displayValue = `
        <div class="flex items-center gap-2">
          <span class="w-5 h-5 flex items-center justify-center rounded border-2 flex-shrink-0 ${checked ? "border-brand-600 bg-brand-600" : "border-gray-300"}">
            ${checked ? '<svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>' : ""}
          </span>
          <span class="text-sm text-gray-700">${checked ? "Yes" : "No"}</span>
        </div>
      `
    } else if (Array.isArray(answer)) {
      displayValue = answer.length > 0
        ? `<div class="flex flex-wrap gap-1.5">${answer.map(a => this.renderAnswerBadge(field, a)).join("")}</div>`
        : `<span class="text-gray-400 italic text-sm">No selection</span>`
    } else if (field.type === "yes_no") {
      const opt = (field.options || []).find(o => o.value === answer)
      const label = opt ? opt.label : answer
      displayValue = `<span class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${answer === "yes" ? "bg-success-50 text-success-700" : "bg-gray-100 text-gray-700"}">${this.esc(label)}</span>`
    } else if (field.type === "opinion_scale") {
      const scale = parseInt(answer) || 0
      const max = field.validation?.max_value || 10
      displayValue = `
        <div class="flex items-center gap-2">
          <span class="text-lg font-bold text-brand-600">${scale}</span>
          <span class="text-sm text-gray-500">/ ${max}</span>
        </div>
      `
    } else if (field.type === "select") {
      displayValue = this.renderAnswerBadge(field, answer)
    } else if (field.type === "textarea") {
      displayValue = `<div class="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg px-3.5 py-2.5 border border-gray-100">${this.esc(String(answer))}</div>`
    } else {
      displayValue = `<span class="text-sm text-gray-800">${this.esc(String(answer))}</span>`
    }

    const typeIcon = this.fieldTypeIcon(field.type)

    return `
      <div class="flex flex-col gap-1.5 pb-5 border-b border-gray-100 last:border-0 last:pb-0">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-gray-400">${index}.</span>
          ${typeIcon}
          <span class="text-sm font-medium text-gray-700">${this.esc(field.label)}</span>
          ${field.required ? '<span class="text-red-400 text-xs">Required</span>' : ""}
        </div>
        <div class="pl-6">${displayValue}</div>
      </div>
    `
  }

  renderAnswerBadge(field, value) {
    // Try to find the option label for the value
    const option = (field.options || []).find(o => o.value === value)
    const label = option ? option.label : value
    return `<span class="inline-flex items-center px-2.5 py-1 rounded-md bg-brand-50 text-brand-700 text-sm font-medium">${this.esc(label)}</span>`
  }

  fieldTypeIcon(type) {
    const icons = {
      text: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>`,
      textarea: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>`,
      select: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4"/></svg>`,
      multi_select: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>`,
      rating: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>`,
      number: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/></svg>`,
      email: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>`,
      date: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>`,
      phone: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>`,
      url: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>`,
      checkbox: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
      yes_no: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
      opinion_scale: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12H4.6C4.04 12 3.76 12 3.546 12.109C3.358 12.205 3.205 12.358 3.109 12.546C3 12.76 3 13.04 3 13.6V19.4C3 19.96 3 20.24 3.109 20.454C3.205 20.642 3.358 20.795 3.546 20.891C3.76 21 4.04 21 4.6 21H9M9 21H15M9 21V8.6C9 8.04 9 7.76 9.109 7.546C9.205 7.358 9.358 7.205 9.546 7.109C9.76 7 10.04 7 10.6 7H13.4C13.96 7 14.24 7 14.454 7.109C14.642 7.205 14.795 7.358 14.891 7.546C15 7.76 15 8.04 15 8.6V21M15 21H19.4C19.96 21 20.24 21 20.454 20.891C20.642 20.795 20.795 20.642 20.891 20.454C21 20.24 21 19.96 21 19.4V4.6C21 4.04 21 3.76 20.891 3.546C20.795 3.358 20.642 3.205 20.454 3.109C20.24 3 19.96 3 19.4 3H16.6C16.04 3 15.76 3 15.546 3.109C15.358 3.205 15.205 3.358 15.109 3.546C15 3.76 15 4.04 15 4.6V8"/></svg>`,
      file_upload: `<svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>`
    }
    return icons[type] || icons.text
  }

  // ==================
  // Reset Response
  // ==================

  showResetModal() {
    if (!this._currentUserId || !this.hasResetModalTarget) return
    // Set participant name in modal
    if (this.hasResetModalNameTarget) {
      const responseData = this.responsesCache?.find(r => r.user_id === this._currentUserId)
      this.resetModalNameTarget.textContent = responseData?.user_name || "this participant"
    }
    this.resetModalTarget.classList.remove("hidden")
  }

  hideResetModal() {
    if (this.hasResetModalTarget) {
      this.resetModalTarget.classList.add("hidden")
    }
  }

  async confirmReset() {
    if (!this._currentUserId) return
    this.hideResetModal()

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.resetUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": token
        },
        body: JSON.stringify({ user_id: this._currentUserId })
      })

      if (!response.ok) return

      const data = await response.json()

      // Remove participant card containing the view button for this user
      const viewBtn = this.element.querySelector(`[data-user-id="${this._currentUserId}"]`)
      if (viewBtn) {
        const card = viewBtn.closest('.flex.items-center.gap-3.p-3')
        if (card) card.remove()
      }

      // Update count
      if (this.hasTotalCountTarget) {
        this.totalCountTarget.textContent = data.total_count
      }

      // Remove from cache
      if (this.responsesCache) {
        this.responsesCache = this.responsesCache.filter(r => r.user_id !== this._currentUserId)
      }

      this.closeDetail()
      this.fetchResponses()
    } catch (err) {
      console.error("Error resetting form response:", err)
    }
  }

  // ==================
  // Panel open/close
  // ==================

  openPanel() {
    if (!this.hasDetailPanelTarget) return
    this.detailPanelTarget.classList.remove("hidden")
    // Trigger reflow then animate
    requestAnimationFrame(() => {
      if (this.hasDetailSlideTarget) {
        this.detailSlideTarget.classList.remove("translate-x-full")
        this.detailSlideTarget.classList.add("translate-x-0")
      }
      if (this.hasDetailBackdropTarget) {
        this.detailBackdropTarget.classList.add("opacity-100")
      }
    })
    // Prevent body scroll
    document.body.style.overflow = "hidden"
  }

  closeDetail() {
    if (!this.hasDetailPanelTarget) return
    if (this.hasDetailSlideTarget) {
      this.detailSlideTarget.classList.remove("translate-x-0")
      this.detailSlideTarget.classList.add("translate-x-full")
    }
    if (this.hasDetailBackdropTarget) {
      this.detailBackdropTarget.classList.remove("opacity-100")
    }
    setTimeout(() => {
      this.detailPanelTarget.classList.add("hidden")
    }, 300)
    document.body.style.overflow = ""
  }

  // ==================
  // Utilities
  // ==================

  esc(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }
}
